// Match a relative named in a document to a person in the open pedigree
// (TGCLOUD-1845, decision 2: attach to an EXISTING relative only).
//
// The extractor says "Mother (Barbara)" or "Maternal aunt"; the pedigree
// knows who the selected person's mother and mother's sisters are. This module
// works out each pedigree member's relationship to the selected person from
// the parent links in `memberRecords` (fatherId / motherId are canvas pids),
// then offers the members whose relationship fits.
//
// A relative is pre-matched only when exactly one saved member fits AND any
// first name the document gives agrees. Everything else is left for the
// clinician to choose — never guessed. A member not yet saved (no
// familyMemberId) cannot be written to and is never offered.

const SEX = { male: 'M', female: 'F', m: 'M', f: 'F' };
const sexOf = (m) => SEX[String(m?.gender || '').toLowerCase()] || null;
const pidOf = (m) =>
  m?.pid === undefined || m?.pid === null ? null : String(m.pid);
const parentPids = (m) =>
  [m?.fatherId, m?.motherId]
    .filter((p) => p !== undefined && p !== null && p !== '')
    .map(String);

const bySex = (m, female, male, neutral) =>
  sexOf(m) === 'F' ? female : sexOf(m) === 'M' ? male : neutral;

/** Is this member saved, i.e. can a record be written to them? */
export function isWritable(m) {
  return !!(m && (m.familyMemberId || m.proBandId));
}

/** The accept-path address of a member. */
export function targetOf(m) {
  return {
    targetFamilyMemberId: m.familyMemberId || m.proBandId,
    targetIsProband: m.proBandId !== undefined && m.proBandId !== null
  };
}

export function memberLabel(m) {
  const name = [m?.firstName, m?.lastName].filter(Boolean).join(' ').trim();
  return name || (m?.upn ? `Unnamed (${m.upn})` : 'Unnamed');
}

/**
 * Each member's relationship to `selected`, as the extractor's relation keys.
 * @returns Map(pid → relKey)
 */
export function relationsTo(selected, members = []) {
  const byPid = new Map(
    members.filter((m) => pidOf(m) !== null).map((m) => [pidOf(m), m])
  );
  const self = byPid.get(pidOf(selected)) || selected;
  const rel = new Map();
  const set = (m, key) => {
    const p = pidOf(m);
    if (p !== null && p !== pidOf(self) && !rel.has(p)) rel.set(p, key);
  };
  const childrenOf = (p) =>
    members.filter((m) => parentPids(m).includes(String(p)));
  const siblingsOf = (m) => {
    const mine = parentPids(m);
    if (!mine.length) return [];
    return members
      .filter(
        (x) =>
          pidOf(x) !== pidOf(m) && parentPids(x).some((p) => mine.includes(p))
      )
      .map((x) => ({
        m: x,
        full:
          mine.length === 2 &&
          parentPids(x).length === 2 &&
          mine.every((p) => parentPids(x).includes(p))
      }));
  };

  const mother =
    self.motherId !== undefined ? byPid.get(String(self.motherId)) : null;
  const father =
    self.fatherId !== undefined ? byPid.get(String(self.fatherId)) : null;
  if (mother) set(mother, 'mother');
  if (father) set(father, 'father');

  const sibs = siblingsOf(self);
  for (const { m, full } of sibs)
    set(
      m,
      full
        ? bySex(m, 'sister', 'brother', 'sibling')
        : bySex(m, 'half-sister', 'half-brother', 'sibling')
    );
  for (const c of childrenOf(pidOf(self)))
    set(c, bySex(c, 'daughter', 'son', 'child'));
  for (const partner of self.spouseIds || []) {
    const p = byPid.get(String(partner));
    if (p) set(p, 'partner');
  }

  for (const [parent, side] of [
    [mother, 'maternal'],
    [father, 'paternal']
  ]) {
    if (!parent) continue;
    const gm =
      parent.motherId !== undefined ? byPid.get(String(parent.motherId)) : null;
    const gf =
      parent.fatherId !== undefined ? byPid.get(String(parent.fatherId)) : null;
    if (gm) set(gm, `${side} grandmother`);
    if (gf) set(gf, `${side} grandfather`);
    for (const { m: au } of siblingsOf(parent)) {
      set(au, bySex(au, `${side} aunt`, `${side} uncle`, `${side} aunt/uncle`));
      for (const cousin of childrenOf(pidOf(au))) set(cousin, `${side} cousin`);
    }
  }
  for (const { m: sib } of sibs)
    for (const c of childrenOf(pidOf(sib)))
      set(c, bySex(c, 'niece', 'nephew', 'niece/nephew'));
  for (const c of childrenOf(pidOf(self)))
    for (const g of childrenOf(pidOf(c))) set(g, 'grandchild');
  return rel;
}

// Does a pedigree relationship fit the relation the document names? A
// document may be less specific ("aunt", "cousin") and the pedigree may not
// know a sex ("maternal aunt/uncle"); a side, where both give one, must agree.
export function fits(pedigreeRel, documentRel) {
  const p = pedigreeRel;
  const d = documentRel;
  if (!p || !d) return false;
  if (p === d) return true;
  const SIBLINGS = [
    'sister',
    'brother',
    'sibling',
    'half-sister',
    'half-brother'
  ];
  if (d === 'sibling') return SIBLINGS.includes(p);
  if ((d === 'sister' || d === 'brother') && p === 'sibling') return true;
  if (d === 'child') return ['daughter', 'son', 'child'].includes(p);
  if ((d === 'daughter' || d === 'son') && p === 'child') return true;
  if (d === 'parents') return p === 'mother' || p === 'father';
  if ((d === 'niece' || d === 'nephew') && p === 'niece/nephew') return true;
  const side = (s) => (/^(maternal|paternal) /.exec(s) || [])[1] || null;
  const bare = (s) => s.replace(/^(maternal|paternal) /, '');
  if (side(d) && side(d) !== side(p)) return false;
  if (bare(d) === bare(p)) return true; // "aunt" fits "maternal aunt"
  return (
    (bare(d) === 'aunt' || bare(d) === 'uncle') && bare(p) === 'aunt/uncle'
  );
}

/**
 * Candidates for a document subject, best first, and the pre-match (or null).
 * @param subject { relation, name }
 */
export function matchSubject(subject, selected, members = []) {
  const rels = relationsTo(selected, members);
  const saved = members
    .filter(isWritable)
    .filter((m) => pidOf(m) !== pidOf(selected));
  const fitting = saved.filter((m) =>
    fits(rels.get(pidOf(m)), subject.relation)
  );
  const name = String(subject.name || '').toLowerCase();
  const named = name
    ? fitting.filter((m) => String(m.firstName || '').toLowerCase() === name)
    : [];
  let preMatch = null;
  if (named.length === 1) preMatch = named[0];
  else if (fitting.length === 1) {
    const only = fitting[0];
    const theirs = String(only.firstName || '').toLowerCase();
    if (!name || !theirs || theirs === name) preMatch = only;
  }
  const others = saved.filter((m) => !fitting.includes(m));
  return {
    preMatch,
    candidates: fitting.map((m) => ({
      member: m,
      relation: rels.get(pidOf(m)) || null,
      fits: true
    })),
    others: others.map((m) => ({
      member: m,
      relation: rels.get(pidOf(m)) || null,
      fits: false
    }))
  };
}

export const memberKey = (m) =>
  String(m?.familyMemberId ?? m?.proBandId ?? m?.pid);
